import { observer } from 'mobx-react';
import { Image, ImageFit } from 'office-ui-fabric-react/lib/Image';
import { AttachmentViewState, ImageLoadState } from 'owa-attachment-data';
import { setThumbnailLoadState } from 'owa-attachment-data';
import refreshThumbnailImageUrl from 'owa-attachment-model-store/lib/actions/refreshThumbnailImageUrl';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import loc from 'owa-localize';
import { thumbnailImagePreviewAltText } from './AttachmentThumbnailView.locstring.json';
import * as React from 'react';
import * as ThrottleThumbnailsManager from '../utils/throttleThumbnails';

const classNames = require('classnames');
const styles = require('./AttachmentThumbnailView.scss');

export interface AttachmentThumbnailProps {
    attachmentViewState: AttachmentViewState;
    thumbnailWidth: number;
    thumbnailHeight: number;
    placeHolderCSSClass?: string;
    imageLoadErrorCSSClass?: string;
    thumbnailUrl?: string;
    onImageLoadError?: () => void;
    fileIconCssClass?: string;
    customFileIconCssClass?: string;
    customThumbnailCssClass?: string;
}

/**
 * The reason we have an internal component state instead of directly using the thumbnail URL from
 * AttachmentModel is that the thumbnailUrl can be passed in as props (e.g. PhotoHub) hence we can't
 * always use the url from the AttachmentViewState. We also use the internal component state to update
 * the thumbnail url once we call the refreshThumbnailImageUrl on the attachment asynchronously.
 *
 * Another reason is to throttle thumbnails request calls. GetAttachmentThumbnail call could
 * be made 50+ times in PhotoHub and listview/attachment well when there are multiple thumbnailable attachments.
 * This is prone to OverBudget exception one server since one mailbox can only have limited mailbox sessions.
 * Most browsers allow 6-13 concurrent connections per domain. So we need throttle requests on client to
 * avoid server errors.
 */
export interface AttachmentThumbnailState {
    thumbnailUrl: string;
    isThrottled: boolean;
}

/**
 * How this component works
 * ------------------------
 *
 * The purpose of this component is to show a thumbnail for an attachment and also a place holder while its being loaded.
 *
 * The multiple states of this view are
 * NotLoaded: The image is being loaded and the placeholder is being displayed to the user
 * Loaded: The image has been successfully loaded and we now display the image to the user
 * ErrorLoading: There was an error loading the image and we just show the placeholder indefinitely
 *
 * The URL of attachment has to be refreshed for classic attachments and hence we have a relatively more complicated
 * component. The other property that is required is that once the thumbnail is rendered we don't re-render again, this
 * is to avoid an flicker that happens becuase the same attachment thumbnail (but with difference URL) was displayed
 * somewhere else in the view.
 *
 * The component does take the `thumbnailUrl` as a prop so that if the creator of this component wants to take ownership
 * of maintaining the thumbnail URL then they can (e.g. Photohub). If the `thumbnailUrl` in the prop is not provided then
 * we refresh the thumbanil image url of that attachment asynchronously and then use the URL that got generated.
 *
 * The `thumbnailUrl` in the state is the one actually being used for rendering the image, it is kept in sync with the
 * `thumbnailUrl` in the prop if provided.
 */

// This component is responsible for showing the attachment thumbnail view.
@observer
export default class AttachmentThumbnailView extends React.Component<
    AttachmentThumbnailProps,
    AttachmentThumbnailState
> {
    constructor(props: AttachmentThumbnailProps) {
        super(props);

        this.state = {
            thumbnailUrl: this.props.thumbnailUrl,
            isThrottled: true,
        };

        if (!this.state.thumbnailUrl) {
            // If the thumbnailUrl in the prop was not provided
            // then refresh the thumbnail image URL of the attachment
            // and use that
            this.tryRefreshThumbnailImageUrl();
        }
    }

    shouldComponentUpdate(
        nextProps: AttachmentThumbnailProps,
        nextState: AttachmentThumbnailState
    ) {
        if (
            this.props.attachmentViewState.thumbnailLoadState !==
            nextProps.attachmentViewState.thumbnailLoadState
        ) {
            // Only render if the thumbnail load state has changed
            return true;
        }

        // If the thumbnail load state has not changed and size properties are the same then don't re-render
        if (
            this.props.thumbnailWidth !== nextProps.thumbnailWidth ||
            this.props.thumbnailHeight !== nextProps.thumbnailHeight
        ) {
            return true;
        }

        if (
            (!this.props.thumbnailUrl && nextProps.thumbnailUrl) ||
            (!this.state.thumbnailUrl && nextState.thumbnailUrl)
        ) {
            // If the first render did not have a thumbnail url and then we get it then re-render
            return true;
        }

        if (this.state.isThrottled !== nextState.isThrottled) {
            // Will render when isThrottled state changed
            return true;
        }

        return false;
    }

    UNSAFE_componentWillReceiveProps(nextProps: AttachmentThumbnailProps) {
        if (this.props.thumbnailUrl !== nextProps.thumbnailUrl) {
            // If the thumbnail URL changed then change the state thumbnail URL
            // which is the one being used to render the image
            this.setState({
                ...this.state,
                thumbnailUrl: nextProps.thumbnailUrl,
            });
        }
    }

    componentDidMount() {
        ThrottleThumbnailsManager.registerCallback(this.setIsThrottledState);
    }

    componentWillUnmount() {
        if (this.state.isThrottled) {
            ThrottleThumbnailsManager.unregisterCallback(this.setIsThrottledState);
        }
    }

    render() {
        const thumbnailLoadState = this.props.attachmentViewState.thumbnailLoadState;

        // Get the right CSS for error loading. If not present, then keep the placeholder CSS as the error
        const errorLoadingCSS = this.props.imageLoadErrorCSSClass
            ? this.props.imageLoadErrorCSSClass
            : this.props.placeHolderCSSClass;

        // If file icon is enabled for document thumbnails, the placeholder and loading error styles are same.
        const placeHolderCSSClass = this.props.fileIconCssClass
            ? this.props.imageLoadErrorCSSClass
            : this.props.placeHolderCSSClass;

        // We do not try to render the image if there was an error loading image earlier.
        // We render place holder when isThrottled or load state is not loaded;
        // while render image when is not throttled and no image loading error.
        return (
            <span className={styles.spanContainer}>
                {(this.state.isThrottled || thumbnailLoadState === ImageLoadState.NotLoaded) && (
                    <div
                        className={styles.notLoadedContainer}
                        style={{
                            height: this.props.thumbnailHeight,
                            width: this.props.thumbnailWidth,
                        }}>
                        <span className={placeHolderCSSClass} />
                    </div>
                )}
                {thumbnailLoadState === ImageLoadState.ErrorLoading && (
                    <div
                        className={styles.errorLoadingContainer}
                        style={{
                            height: this.props.thumbnailHeight,
                            width: this.props.thumbnailWidth,
                        }}>
                        <span className={errorLoadingCSS} />
                    </div>
                )}
                {thumbnailLoadState !== ImageLoadState.ErrorLoading && (
                    <Image
                        src={this.state.isThrottled ? '' : this.state.thumbnailUrl}
                        onError={this.handleImageError}
                        onLoad={this.onImageLoad}
                        width={this.props.thumbnailWidth}
                        height={this.props.thumbnailHeight}
                        className={
                            this.props.customThumbnailCssClass
                                ? this.props.customThumbnailCssClass
                                : styles.thumbnail
                        }
                        imageFit={this.props.fileIconCssClass ? ImageFit.contain : ImageFit.cover}
                        alt={loc(thumbnailImagePreviewAltText)}
                    />
                )}
                {thumbnailLoadState === ImageLoadState.Loaded && this.props.fileIconCssClass && (
                    <span
                        style={
                            this.props.customFileIconCssClass
                                ? null
                                : {
                                      position: 'absolute',
                                      top: 5 - this.props.thumbnailHeight,
                                      left: 5,
                                  }
                        }
                        className={classNames(
                            this.props.fileIconCssClass,
                            this.props.customFileIconCssClass
                        )}
                    />
                )}
            </span>
        );
    }

    private tryRefreshThumbnailImageUrl = async () => {
        const {
            attachmentId,
            isReadOnly,
            isPlaceholderAttachment,
        } = this.props.attachmentViewState;

        if (!isPlaceholderAttachment) {
            // Only refresh the thumbnail url if it is not a placeholder attachment.
            // As placeholder attachment would not have a thumbnail.
            await refreshThumbnailImageUrl(attachmentId, isReadOnly);
            const url = getAttachment(attachmentId).thumbnailImage.url;

            // Set the URL once it is refreshed
            this.setState({
                ...this.state,
                thumbnailUrl: url,
            });
        }
    };

    private handleImageError = (ev: any) => {
        if (!this.state.isThrottled) {
            ThrottleThumbnailsManager.displayNextThumbnail();

            setThumbnailLoadState(this.props.attachmentViewState, ImageLoadState.ErrorLoading);
            if (this.props.onImageLoadError) {
                this.props.onImageLoadError();
            }
        }
    };

    private onImageLoad = (ev: any) => {
        if (!this.state.isThrottled) {
            ThrottleThumbnailsManager.displayNextThumbnail();

            setThumbnailLoadState(this.props.attachmentViewState, ImageLoadState.Loaded);
        }
    };

    private setIsThrottledState = (isThrottled: boolean) => {
        this.setState({
            ...this.state,
            isThrottled: isThrottled,
        });
    };
}
